import { useEffect, useState } from 'react';
import '../../styles/onboardingFlow/signUp/signUp.scss';
import { useNavigate } from 'react-router-dom';
import store from '../../store/store';
import { useTranslation } from 'react-i18next';
import MainSquareButton from '../../ui/buttons/MainSquareButton';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { requestUserSignUp } from '../../lib/api/sign-up';
import FooterLinks from '../../components/footer/FooterLinks';
import useTitle from '../../customHooks/useTitle';
import AINormalInput from '../../ui/inputs/AINormalInput';
import CustomSVGs from '../../ui/customSVGs/CustomSVGs';
import { crossSVG, tickSVG } from '../../lib/allCustomSVGs';
import { clearUserSlice, setReferral } from '../../store/reducers/userSlice';
import { RequestInfo } from '../../interfaces/RequestInfo';
import { useGoogleLogin } from '@react-oauth/google';
import { asyncHandler } from '../../lib/api/async-handler';
import { submitLogToBugsnag } from '../../lib/api/log';
import { LOGIN_API } from '../../lib/constants/central-api';
import { applicationWWWXRLEncodedBodyBuilder } from '../../lib/utils/body-builder';
import { setIsInitSuccess, setMessage } from '../../store/reducers/appSlice';
import isNotEmptyString from '../../lib/helper-functions/isNotEmptyString';
import { setAccessToken } from '../../store/reducers/authSlice';
import moment from 'moment';
import { fetchAppInit } from '../../lib/api/app-init';
import googleButtonImg from '../../assets/googleIcon.png';
import { AnimatePresence, motion } from 'framer-motion';
import adupLogoSet from '../../assets/onboarding/common/adup-logo-set.png';
import MobileLogoCard from '../mobileLogoCard/MobileLogoCard';

const SignUp = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  useTitle(t('Register'));
  const params = new URLSearchParams(window.location.search);

  const authToken = store.getState().auth.authorization.accessToken;
  const referral = useSelector((state: RootState) => state.user?.referral);
  const preUserInvitation = useSelector(
    (state: RootState) => state.user.preUserInvitation
  );

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isPasswordInputFocused, setIsPasswordInputFocused] = useState(false);
  const [loading, setLoading] = useState(false);

  const [hasUppercase, setHasUppercase] = useState<boolean>(false);
  const [hasLowercase, setHasLowercase] = useState<boolean>(false);
  const [hasNumber, setHasNumber] = useState<boolean>(false);
  const [hasSpecialChar, setHasSpecialChar] = useState<boolean>(false);
  const [isLongEnough, setIsLongEnough] = useState<boolean>(false);

  const validatePassword = (password: string) => {
    setHasUppercase(/[A-Z]/.test(password));
    setHasLowercase(/[a-z]/.test(password));
    setHasNumber(/[0-9]/.test(password));
    setHasSpecialChar(/[^a-zA-Z0-9]/.test(password));
    setIsLongEnough(password.length >= 8);
  };

  const showPasswordValidationView =
    !hasUppercase ||
    !hasLowercase ||
    !hasNumber ||
    !hasSpecialChar ||
    !isLongEnough;

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newPassword = event.target.value;
    setPassword(newPassword);
    validatePassword(newPassword);
  };

  useEffect(() => {
    // Check if the referral exists in the URL
    const referralParam = params.get('referral');
    const emailParam = params.get('email');

    // If referral exists in URL and it's not already in Redux, update Redux state
    if (referralParam && typeof referralParam === 'string') {
      dispatch(setReferral(referralParam));
    } else {
      dispatch(setReferral(''));
    }

    // If email parameter exists in URL, auto-fill the email input
    if (emailParam && typeof emailParam === 'string') {
      setEmail(emailParam);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (
      preUserInvitation.id &&
      preUserInvitation.data?.email &&
      isNotEmptyString(preUserInvitation.data?.email)
    ) {
      setEmail(preUserInvitation.data?.email);
    }
  }, [preUserInvitation]);

  const isDisabled =
    !hasUppercase ||
    !hasLowercase ||
    !hasNumber ||
    !hasSpecialChar ||
    !isLongEnough ||
    email === '' ||
    !/^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}(?:\.[a-zA-Z]{2,})?$/.test(email);

  async function handleSignUpClick(e: any) {
    setLoading(true);
    e.preventDefault();

    // clear all existing user data before sign up
    dispatch(clearUserSlice());

    const sendingData: { email: string; password: string; [key: string]: any } =
      {
        email: email,
        password: password,
      };

    if (referral && referral !== '') {
      sendingData.is_referred = 1;
      sendingData.referral_code = referral;
    }

    const isRegistrationSuccess = await requestUserSignUp(sendingData);

    if (!isRegistrationSuccess) {
      setLoading(false);
    }
  }

  const normalLoginProcess = (response: any, isWebAuthnLogin?: boolean) => {
    store.dispatch(setIsInitSuccess(false));
    const token_type = 'Bearer';
    const { access_token } = response?.data;

    if (isNotEmptyString(access_token)) {
      store.dispatch(
        setAccessToken({
          sellerToken: access_token,
          type: token_type,
          expiresOn: moment().add(21, 'days'),
        })
      );
      navigate('/onboarding');

      //make init call to get all info about logged-In user
      fetchAppInit();
    } else {
      console.log('Access Token is empty');
      submitLogToBugsnag('error', `Access Token is empty`);
      store.dispatch(
        setMessage({
          message: t('UnauthorizedAccess'),
          messageType: 'error',
        })
      );
    }
  };

  const googleSignIn = async (googleAccessToken: any) => {
    try {
      const sendingData: {
        oauth: string;
        role: string;
        token: string;
        [key: string]: any;
      } = {
        oauth: 'google',
        role: 'seller',
        token: googleAccessToken,
        bypass: params.get('bypass') === 'true',
      };

      if (referral && referral !== '') {
        sendingData.is_referred = 1;
        sendingData.referral_code = referral;
      }

      const requestInfo: RequestInfo = {
        url: LOGIN_API,
        method: 'POST',
        body: applicationWWWXRLEncodedBodyBuilder(sendingData),
        headers: {
          Accept: 'application/json',
          'Content-type': 'application/x-www-form-urlencoded',
        },
      };

      const actionType = 'LOGIN_VERIFICATION';
      const customMsg = t('LoginSuccess-msg');

      const res: any = await asyncHandler(
        requestInfo,
        actionType,
        customMsg,
        false,
        true
      );

      const response = res.data;

      if (res.actionType === actionType && response.success === true) {
        normalLoginProcess(response);
      } else {
        store.dispatch(
          setMessage({
            message: response.message,
            messageType: 'error',
          })
        );
      }
    } catch (exception) {
      console.log('Error during authentication request');
      submitLogToBugsnag('error', `${exception}`);
    }
  };

  const googleAuthentication = useGoogleLogin({
    onSuccess: (tokenResponse) => {
      googleSignIn(tokenResponse?.access_token);
    },
  });

  const passwordValidationView = () => {
    return (
      <motion.div
        initial={{ opacity: 0.1 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0.1 }}
        transition={{ duration: 0.3 }}
        className='onboarding-signup__validation'
      >
        <div className='onboarding-signup__validation-item'>
          <div className='onboarding-signup__validation-item-icon'>
            {hasUppercase ? (
              <CustomSVGs fillColor={'#28a745'} svg={tickSVG} />
            ) : (
              <div className='onboarding-signup__validation-item-icon-x'>
                <CustomSVGs fillColor={'#dc3545'} svg={crossSVG} />
              </div>
            )}
          </div>
          <p>{t('AtLeastOneUppercaseLetter')}</p>
        </div>
        <div className='onboarding-signup__validation-item'>
          <div className='onboarding-signup__validation-item-icon'>
            {hasLowercase ? (
              <CustomSVGs fillColor={'#28a745'} svg={tickSVG} />
            ) : (
              <div className='onboarding-signup__validation-item-icon-x'>
                <CustomSVGs fillColor={'#dc3545'} svg={crossSVG} />
              </div>
            )}
          </div>
          <p>{t('AtLeastOneLowercaseLetter')}</p>
        </div>
        <div className='onboarding-signup__validation-item'>
          <div className='onboarding-signup__validation-item-icon'>
            {hasNumber ? (
              <CustomSVGs fillColor={'#28a745'} svg={tickSVG} />
            ) : (
              <div className='onboarding-signup__validation-item-icon-x'>
                <CustomSVGs fillColor={'#dc3545'} svg={crossSVG} />
              </div>
            )}
          </div>
          <p>{t('AtLeastOneNumber')}</p>
        </div>
        <div className='onboarding-signup__validation-item'>
          <div className='onboarding-signup__validation-item-icon'>
            {hasSpecialChar ? (
              <CustomSVGs fillColor={'#28a745'} svg={tickSVG} />
            ) : (
              <div className='onboarding-signup__validation-item-icon-x'>
                <CustomSVGs fillColor={'#dc3545'} svg={crossSVG} />
              </div>
            )}
          </div>
          <p>{t('AtLeastOneSpecialCharacter')}</p>
        </div>
        <div className='onboarding-signup__validation-item'>
          <div className='onboarding-signup__validation-item-icon'>
            {isLongEnough ? (
              <CustomSVGs fillColor={'#28a745'} svg={tickSVG} />
            ) : (
              <div className='onboarding-signup__validation-item-icon-x'>
                <CustomSVGs fillColor={'#dc3545'} svg={crossSVG} />
              </div>
            )}
          </div>
          <p>{t('AtLeast8Characters')}</p>
        </div>
      </motion.div>
    );
  };

  if (!authToken.sellerToken) {
    return (
      <div className='onboarding-signup'>
        <div className='onboarding-signup__container'>
          {/* Mobile logo card (slideshow, mobile only) */}
          <MobileLogoCard />

          {/* Logo (desktop only; hidden on mobile when card is shown) */}
          <div className='onboarding-signup__logo'>
            <img src={adupLogoSet} alt='AdUp Logo' />
          </div>

          {/* Heading */}
          <div className='onboarding-signup__header'>
            <h1>{t('SignUp-new-ui')}</h1>
            <p>{t('SignUpSubText-new-ui')}</p>
          </div>

          {/* Form */}
          <form
            onSubmit={handleSignUpClick}
            className='onboarding-signup__form'
          >
            {/* Email Input */}
            <div className='onboarding-signup__field'>
              <AINormalInput
                id='sign-up-form-email'
                value={email}
                type='email'
                changeListeners={[(e) => setEmail(e.target.value)]}
                placeholder={t('EmailAddress')}
                innerPlaceholder={'you@example.com'}
                required={true}
                pattern='^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$'
              />
            </div>

            {/* Password Input */}
            <div className='onboarding-signup__field onboarding-signup__field--password'>
              <AINormalInput
                id='sign-up-form-password'
                value={password}
                changeListeners={[(e) => handleChange(e)]}
                placeholder={t('Password')}
                type='password'
                innerPlaceholder={t('ChooseAPassword')}
                required={true}
                focusListeners={[
                  (e) => {
                    setIsPasswordInputFocused(true);
                  },
                ]}
                blurListeners={[
                  (e) => {
                    setIsPasswordInputFocused(false);
                  },
                ]}
              />
              {/* Password Validation - Floating */}
              <AnimatePresence exitBeforeEnter>
                {isPasswordInputFocused &&
                  showPasswordValidationView &&
                  passwordValidationView()}
              </AnimatePresence>
            </div>

            {/* Continue Button */}
            <div className='onboarding-signup__button'>
              <MainSquareButton
                value={t('Continue')}
                type='submit'
                loading={loading}
                disabled={isDisabled}
                classes='onboarding-signup__continue-btn'
              />
            </div>

            {/* OR Separator */}
            <div className='onboarding-signup__separator'>
              <span>{t('OR')}</span>
            </div>

            {/* Google Button */}
            <div className='onboarding-signup__button'>
              <MainSquareButton
                value={t('ContinueWithGoogle')}
                image={googleButtonImg}
                type='button'
                onClick={() => {
                  googleAuthentication();
                }}
                classes='onboarding-signup__google-btn'
              />
            </div>

            {/* Sign In Link */}
            <div className='onboarding-signup__signin'>
              <span>
                {t('AlreadyHaveAnAccount')}{' '}
                <span
                  className='onboarding-signup__signin-link'
                  onClick={() => navigate('/login')}
                >
                  {t('SignIn')}
                </span>
              </span>
            </div>

            {/* Terms and Privacy */}
            <div className='onboarding-signup__terms'>
              {/* <span>{t('ByContinuingYouAgree')} </span> */}
              <FooterLinks isRegisterPage={true} />
            </div>
          </form>
        </div>

        {/* Copyright */}
        <div className='onboarding-signup__copyright'>
          <span>© AdUp {new Date().getFullYear()}</span>
        </div>
      </div>
    );
  } else {
    window.location.assign('/');
    return null;
  }
};

export default SignUp;
